/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Evgeniy Naumov
 * @version 1.22.0
 * @since 1.22.0
 */

import { FC, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import PageLocale from '@/main/modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '@/main/modules/internationalization/constants';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardOrderTemplate,
} from '@/main/modules/internationalization/intSelectors';
import { EWizardName } from '@/main/modules/wizard/wizardConstants';
import Title from '@components/common/Title/Title';
import { EBasketType } from '@modules/app/appConstants';
import { ECommonTranslationPages } from '@modules/internationalization/types/common/pages';
import Wizard from '@modules/wizard/components/Wizard';
import { makeSelectWizardStepName } from '@modules/wizard/wizardSelectors';

import { wizardOrderTemplateHandleNextAction } from '../actions';
import { EWizardOrderTemplateSteps } from '../constants';
import { makeSelectWizardOrderTemplateValidation } from '../selectors';
import { IWizardOrderTemplateLocationData } from '../types';
import WizardOrderTemplateParametersConfirmStep from './WizardOrderTemplateParametersConfirmStep';
import WizardOrderTemplateSelectionDataStep from './WizardOrderTemplateSelectionDataStep';

const WizardOrderTemplate: FC = () => {
    const dispatch = useDispatch();
    const { state } = useLocation<IWizardOrderTemplateLocationData>();

    const tCommon = useSelector(makeSelectTranslationCommon());
    const wizardTranslation = useSelector(makeSelectTranslationWizardOrderTemplate());
    const validation = useSelector(makeSelectWizardOrderTemplateValidation());
    const stepName = useSelector(makeSelectWizardStepName());

    const handleNext = () => dispatch(wizardOrderTemplateHandleNextAction());

    const pageTitle =
        state.mode === 'TEMPLATE_CREATE'
            ? ECommonTranslationPages.wizardOrderTemplateCreate
            : ECommonTranslationPages.wizardOrderTemplateEdit;

    const tTitle = tCommon.pages[pageTitle];

    const Step = useMemo(() => {
        switch (stepName) {
            case EWizardOrderTemplateSteps.TemplateSelectionDataStep:
                return <WizardOrderTemplateSelectionDataStep templateData={state}/>;
            case EWizardOrderTemplateSteps.TemplateParametersConfirmStep:
                return <WizardOrderTemplateParametersConfirmStep />;
        }
    }, [stepName, state]);

    return (
        <>
            <PageLocale
                lazy
                fileNames={[TranslationFileNamesEnum.wizardOrderTemplate, TranslationFileNamesEnum.wizard]}
            />
            <Title pageName={pageTitle} />
            <Wizard
                basketType={EBasketType.MOBILE}
                isNextDisabled={!validation?.isValid}
                name={EWizardName.ORDER_TEMPLATE}
                translationProps={{
                    error: wizardTranslation.error,
                    steps: wizardTranslation.steps,
                    title: tTitle,
                }}
                validateError={validation?.error}
                onWizardNextHandler={handleNext}
            >
                {Step}
            </Wizard>
        </>
    );
};

export default WizardOrderTemplate;
